import { handoff, link1, login, logout, pin, resetPin1, session, setPin, signup } from "@/generated/api/sdk.gen"
import type { OAuth2PinGrant, SetPinRequest, SignupRequest, WebResetPinRequest } from "@/generated/api/types.gen"
import type { Api } from "@/platform/api/api"
import { bearer, body, conversionHeaders } from "@/platform/api/gateways/common"
import { confirmedWebSession, type WebSession } from "@/platform/api/session-contract"

type PinGrant = Pick<OAuth2PinGrant, "identifier" | "pin"> & { verificationToken: string }

export const sessionGateway = (api: Api) => ({
  probe: (signal?: AbortSignal): Promise<WebSession> => body(session(api.request({ credentials: "cookie-session", signal }))),

  loginWithPin: async ({ identifier, pin: secret, verificationToken }: PinGrant): Promise<WebSession> =>
    confirmedWebSession(
      await body(pin({ ...api.request({ credentials: "verification-bearer", setsSession: true, headers: bearer(verificationToken) }), body: { identifier, pin: secret } }))
    ),

  redeemMagicLink: async (token: string): Promise<WebSession> =>
    confirmedWebSession(await body(link1({ ...api.request({ credentials: "public", setsSession: true }), body: { token } }))),

  createHandoff: async (accessToken: string | null, signal?: AbortSignal): Promise<string> => {
    const credentials = accessToken
      ? ({ credentials: "verification-bearer", cache: "no-store", headers: bearer(accessToken) } as const)
      : ({ credentials: "cookie-session" } as const)
    const { code } = await body(handoff(api.request({ ...credentials, signal })))
    if (!code) throw new Error("Invalid web handoff response")
    return code
  },

  redeemHandoff: async (code: string): Promise<WebSession> =>
    confirmedWebSession(await body(login({ ...api.request({ credentials: "public", setsSession: true }), body: { code } }))),

  setPin: async (request: SetPinRequest, verificationToken: string): Promise<WebSession> =>
    confirmedWebSession(await body(setPin({ ...api.request({ credentials: "verification-bearer", setsSession: true, headers: bearer(verificationToken) }), body: request }))),

  resetPin: async (request: WebResetPinRequest, verificationToken: string): Promise<WebSession> =>
    confirmedWebSession(await body(resetPin1({ ...api.request({ credentials: "verification-bearer", setsSession: true, headers: bearer(verificationToken) }), body: request }))),

  signup: async (request: SignupRequest, conversionId?: string, verificationToken?: string): Promise<WebSession> =>
    confirmedWebSession(
      await body(
        signup({
          ...api.request({
            credentials: "verification-bearer",
            setsSession: true,
            headers: { ...conversionHeaders(conversionId), ...(verificationToken ? bearer(verificationToken) : {}) },
          }),
          body: request,
        })
      )
    ),

  revoke: async (): Promise<void> => {
    await logout(api.request({ credentials: "cookie-session" }))
  },
})

export type SessionGateway = ReturnType<typeof sessionGateway>
